// ============================================================
// TaskFlow OS - Glass Modal Component
// ============================================================

import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IconX } from '../../icons/SvgIcons';

interface GlassModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  footer?: React.ReactNode;
}

const SIZE_CLASSES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

const GlassModal: React.FC<GlassModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  size = 'md',
  footer,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={overlayRef}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
          style={{
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
          }}
        >
          <motion.div
            className={`${SIZE_CLASSES[size]} w-full`}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          >
            <div
              className="rounded-2xl overflow-hidden"
              style={{
                background: 'rgba(10, 15, 30, 0.9)',
                backdropFilter: 'blur(30px) saturate(1.8)',
                WebkitBackdropFilter: 'blur(30px) saturate(1.8)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: `
                  inset 0 1px 0 0 rgba(255, 255, 255, 0.1),
                  inset 0 -2px 8px 0 rgba(0, 0, 0, 0.2),
                  0 24px 80px -12px rgba(0, 0, 0, 0.5)
                `,
              }}
            >
              {/* Refraction */}
              <div className="absolute top-0 left-0 right-0 h-1/3 pointer-events-none rounded-t-2xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, transparent 100%)' }} />

              {/* Header */}
              <div className="relative flex items-start justify-between px-6 pt-5 pb-3">
                <div>
                  <h2 className="text-base font-bold text-white">{title}</h2>
                  {subtitle && <p className="text-xs text-white/40 mt-0.5">{subtitle}</p>}
                </div>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <IconX size={18} className="text-white/40" />
                </button>
              </div>

              {/* Content */}
              <div className="relative px-6 pb-4 max-h-[60vh] overflow-y-auto">
                {children}
              </div>

              {/* Footer */}
              {footer && (
                <div className="relative px-6 py-4 border-t border-white/5 flex items-center justify-end gap-3">
                  {footer}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GlassModal;
